import { useState, type CSSProperties, type ReactElement } from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';

import { V4 } from '../../../tokens';

export interface LowExtractionPromptProps {
  itemsExtracted: number;
  onContinue: () => void;
  onAddManually: () => void;
}

const cardStyle: CSSProperties = {
  maxWidth: 480,
  margin: '0 auto',
  padding: 32,
  background: V4.white,
  borderRadius: 12,
  border: `1px solid ${V4.grayLight}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 16,
};

const titleStyle: CSSProperties = {
  color: V4.primary,
  fontSize: 20,
  fontWeight: 600,
  margin: 0,
};

const bodyTextStyle: CSSProperties = {
  color: V4.primary,
  fontSize: 14,
  margin: 0,
  lineHeight: 1.5,
};

const hintStyle: CSSProperties = {
  color: V4.grayUltraDark,
  fontSize: 13,
  margin: 0,
  lineHeight: 1.5,
};

const primaryButtonStyle: CSSProperties = {
  background: V4.secondary,
  color: V4.white,
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'background 160ms cubic-bezier(.4,0,.2,1)',
  minWidth: 200,
};

const secondaryButtonStyle: CSSProperties = {
  background: 'transparent',
  color: V4.primary,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'background 160ms cubic-bezier(.4,0,.2,1)',
  minWidth: 200,
};

export const LowExtractionPrompt = ({
  itemsExtracted,
  onContinue,
  onAddManually,
}: LowExtractionPromptProps): ReactElement => {
  const [primaryHover, setPrimaryHover] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);

  const plural = itemsExtracted === 1 ? '' : 's';

  return (
    <div data-testid="low-extraction-prompt" style={cardStyle}>
      <IconAlertTriangle size={48} color={V4.warning} />
      <h2 style={titleStyle}>Encontramos pocos productos</h2>
      <p style={bodyTextStyle}>
        Solo detectamos {itemsExtracted} producto{plural} en tus archivos. Esto puede deberse a fotos poco claras o un
        menú difícil de leer.
      </p>
      <p style={hintStyle}>Puedes continuar con lo que detectamos o agregar tu menú manualmente.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', alignItems: 'center' }}>
        <button
          type="button"
          data-testid="low-extraction-continue"
          style={{
            ...primaryButtonStyle,
            background: primaryHover ? V4.secondaryDark : V4.secondary,
          }}
          onClick={onContinue}
          onMouseEnter={() => setPrimaryHover(true)}
          onMouseLeave={() => setPrimaryHover(false)}
        >
          Continuar con estos
        </button>
        <button
          type="button"
          data-testid="low-extraction-manual"
          style={{
            ...secondaryButtonStyle,
            background: secondaryHover ? V4.grayUltraLight : 'transparent',
          }}
          onClick={onAddManually}
          onMouseEnter={() => setSecondaryHover(true)}
          onMouseLeave={() => setSecondaryHover(false)}
        >
          Agregar manualmente
        </button>
      </div>
    </div>
  );
};
